/**
 * ************************************
 *
 * @module  Movie.saga.js
 * @author  Matt P
 * @date    12/27/2020
 * @description redux saga file for the Movie component
 *
 * ************************************
 */
// ----------------------------------------------------------------------------|
//                                  Imports
// ----------------------------------------------------------------------------|
import { call, put } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import ACTIONS from 'store/actions/actionTypes';

import API from 'API';

import { GENERIC } from 'constants.js';

import { getPartsFromUrl } from 'utils/utils';

import { reportAPIError } from 'utils/ErrorHandlingUtils';

// ----------------------------------------------------------------------------|
//                                Utilities
// ----------------------------------------------------------------------------|
/**
 * @description formats data
 *
 * @param {Array} data - Array of Objects
 *
 * @returns {Array}
 */
const formatData = (data = []) => {
  if (typeof data[0] === 'object') {
    return data.map((each) => each.name);
  }
  return data;
};

// ----------------------------------------------------------------------------|
//                              Sagas - Movie
// ----------------------------------------------------------------------------|
/**
 * @description handles uploading an image
 *
 * @param {Object} action - object passed from the redux action
 */
function* uploadImage(action) {
  const { IMAGE_UPLOAD_FAIL } = GENERIC;
  const { postData } = window.axios;
  const { file, fileId, photo_metadata } = action.data;

  try {
    const response = yield call(postData, API.movieImage, file, {
      'Content-Type': 'multipart/form-data',
    });

    if (response.status === 200) {
      yield put({
        type: ACTIONS.MOVIE.UPLOAD_IMAGE_SUCCESS,
        data: { ...response.data, fileId, photo_metadata },
      });
    } else {
      toast.error(IMAGE_UPLOAD_FAIL);
      yield put({ type: ACTIONS.MOVIE.UPLOAD_IMAGE_FAIL });
    }
  } catch (error) {
    toast.error(IMAGE_UPLOAD_FAIL);
    yield put({ type: ACTIONS.MOVIE.UPLOAD_IMAGE_FAIL });
  }
}

/**
 * @description handles updating data
 *
 * @param {Object} action - object passed from the redux action
 * @param {String} id - id of action
 */
function* updatedData(data, id) {
  const { status: toBeStatus } = data;
  const { secondPart: currentTab } = getPartsFromUrl(window.location.pathname);

  const currentStatus = currentTab === 'drafts' ? 'draft' : currentTab;

  if (toBeStatus === currentStatus) {
    yield put({ type: ACTIONS.MOVIES.MOVE_TO_TOP, data: { id, data } });
  } else {
    yield put({ type: ACTIONS.MOVIES.REMOVE_MOVIE, data: { id } });
  }
}

/**
 * @description Fetch Movie object using ID
 *
 * @param {Object} action - object passed from the redux action
 */
function* fetchMovie(action) {
  const { getData } = window.axios;

  try {
    const URL = `${API.movieDetail}${action.data.movieId}`;
    const response = yield call(getData, URL);

    if (response.status === 200) {
      yield put({
        type: ACTIONS.MOVIE.FETCH_MOVIE_SUCCESS,
        data: response.data,
      });
    } else {
      yield put({ type: ACTIONS.MOVIE.FETCH_MOVIE_FAIL });
    }
  } catch (error) {
    yield put({ type: ACTIONS.MOVIE.FETCH_MOVIE_FAIL });
  }
}

/**
 * @description saves data to DB
 *
 * @param {Object} action - object passed from the redux action
 */
function* saveData(action) {
  const { MOVIE_SAVE_SUCCESS, MOVIE_SAVE_FAIL } = GENERIC;
  const { putData } = window.axios;
  const data = { ...action.data };

  yield put({
    type: ACTIONS.MOVIE.BUTTON_DISBALING,
    data: { isBtnDisabled: true },
  });

  try {
    let response;

    if (action.id)
      // update
      response = yield call(putData, `${API.movieDetail}${action.id}`, data);
    else {
      // create
      response = yield call(putData, API.movieList, data);
    }

    if (response.status === 200) {
      yield put({
        type: ACTIONS.MOVIE.SAVE_DATA_SUCCESS,
        data: { ...response.data },
      });

      yield updatedData(response.data, action.id);

      if (data.should_refetch_3rd_parties_content) {
        const currTab =
          data.status === 'fetched' ||
          data.status === 'queue' ||
          data.status === 'draft'
            ? 'drafts'
            : data.status;

        action.successCB(response.data.id, currTab);
      } else {
        toast.success(MOVIE_SAVE_SUCCESS);
        action.successCB();
      }
    } else if (
      // if duplicate FWD to that ID
      response.status === 400 &&
      response.data.error_code === 'DUPLICATED_TMDB_ID'
    ) {
      toast.success('Card ID already exists, redirecting');

      const currTab = data.status === 'draft' ? 'drafts' : data.status;

      const duplicateId = response.data.message.split(' ').pop();

      action.successCB(duplicateId, currTab);
    } else {
      reportAPIError('Movie Save - HTTP', response.data);

      yield put({ type: ACTIONS.MOVIE.SAVE_DATA_FAIL });
      toast.error(MOVIE_SAVE_FAIL);
    }
  } catch (error) {
    reportAPIError('Overall Movie Save Catch', error);

    yield put({ type: ACTIONS.MOVIE.SAVE_DATA_FAIL });
    toast.error(MOVIE_SAVE_FAIL);
  } finally {
    yield put({
      type: ACTIONS.MOVIE.BUTTON_DISBALING,
      data: { isBtnDisabled: false },
    });
  }
}

// ----------------------------------------------------------------------------|
//                                  Export
// ----------------------------------------------------------------------------|
export { uploadImage, fetchMovie, saveData };
